import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SchemaOrg from '@/components/feature/SchemaOrg';
import { comics, categories } from '@/mocks/comics';

function getCategoryName(slug: string): string {
  const cat = categories.find(c => c.slug === slug);
  return cat ? cat.name : slug;
}

export default function ComicDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [currentPage, setCurrentPage] = useState(1);
  const chaptersPerPage = 20;

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [id]);

  const comic = comics.find(c => c.id === Number(id));

  if (!comic) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20">
          <div className="px-4 md:px-6 text-center py-20">
            <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
              <i className="ri-emotion-sad-line text-3xl text-foreground-400"></i>
            </div>
            <h1 className="text-xl font-bold text-foreground-900 mb-2">漫画未找到</h1>
            <p className="text-sm text-foreground-500 mb-6">该漫画不存在或已被移除</p>
            <Link
              to="/category/all"
              className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-500 text-white rounded-full text-sm font-medium hover:bg-primary-600 transition-all whitespace-nowrap"
            >
              <i className="ri-arrow-left-line"></i>
              返回漫画列表
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const totalPages = Math.ceil(comic.chapters_list.length / chaptersPerPage);
  const startIdx = (currentPage - 1) * chaptersPerPage;
  const displayedChapters = comic.chapters_list.slice(startIdx, startIdx + chaptersPerPage);

  const pageNumbers: number[] = [];
  const maxVisiblePages = 5;
  let pageStart = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
  let pageEnd = Math.min(totalPages, pageStart + maxVisiblePages - 1);
  if (pageEnd - pageStart + 1 < maxVisiblePages) {
    pageStart = Math.max(1, pageEnd - maxVisiblePages + 1);
  }
  for (let i = pageStart; i <= pageEnd; i++) {
    pageNumbers.push(i);
  }

  const relatedComics = comics
    .filter(c => c.category === comic.category && c.id !== comic.id)
    .slice(0, 4);

  return (
    <>
      <SchemaOrg
        type="ComicSeries"
        data={{
          name: comic.title,
          description: comic.description,
          url: `https://yaojingdongman.com/comic/${comic.id}`,
          image: comic.cover,
          author: { '@type': 'Person', name: comic.author },
          publisher: { '@type': 'Organization', name: '妖精动漫' },
          numberOfEpisodes: comic.chapters,
          aggregateRating: {
            '@type': 'AggregateRating',
            ratingValue: comic.rating,
            bestRating: '10',
            ratingCount: Math.floor(comic.views / 100),
          },
        }}
      />
      <SchemaOrg
        type="BreadcrumbList"
        data={{
          itemListElement: [
            {
              '@type': 'ListItem',
              position: 1,
              name: '首页',
              item: 'https://yaojingdongman.com',
            },
            {
              '@type': 'ListItem',
              position: 2,
              name: getCategoryName(comic.category),
              item: `https://yaojingdongman.com/category/${comic.category}`,
            },
            {
              '@type': 'ListItem',
              position: 3,
              name: comic.title,
            },
          ],
        }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20">
          <div className="px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: getCategoryName(comic.category), href: `/category/${comic.category}` },
                { label: comic.title },
              ]}
            />

            <div className="flex flex-col lg:flex-row gap-8 mb-12">
              <div className="flex-shrink-0">
                <div className="w-48 md:w-56 rounded-xl overflow-hidden">
                  <img
                    src={comic.cover}
                    alt={comic.title}
                    className="w-full aspect-[3/4] object-cover object-top"
                  />
                </div>
              </div>

              <div className="flex-1">
                <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950 mb-2">
                  {comic.title}
                </h1>
                {comic.titleEn && (
                  <p className="text-sm text-foreground-500 mb-4">{comic.titleEn}</p>
                )}

                <div className="flex flex-wrap items-center gap-4 mb-4">
                  <span className="flex items-center gap-1 text-sm text-accent-600 font-semibold">
                    <i className="ri-star-fill"></i>
                    {comic.rating}
                  </span>
                  <span className="text-sm text-foreground-500">
                    {(comic.views / 10000).toFixed(1)}万次阅读
                  </span>
                  <span className="text-sm text-foreground-500">
                    共{comic.chapters}话
                  </span>
                  <span
                    className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      comic.status === '连载中'
                        ? 'bg-accent-100 text-accent-700'
                        : 'bg-secondary-100 text-secondary-700'
                    }`}
                  >
                    {comic.status}
                  </span>
                </div>

                <div className="flex flex-wrap items-center gap-2 mb-4">
                  <span className="text-sm text-foreground-500">作者：</span>
                  <span className="text-sm text-foreground-900 font-medium">{comic.author}</span>
                </div>

                <div className="flex flex-wrap gap-2 mb-6">
                  {comic.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1 bg-background-100 text-foreground-600 rounded-full text-xs"
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                <p className="text-sm text-foreground-700 leading-relaxed mb-6">
                  {comic.longDescription}
                </p>

                <div className="flex flex-wrap gap-3">
                  <button className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-500 text-white rounded-full text-sm font-medium hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer">
                    <i className="ri-play-circle-line"></i>
                    开始阅读
                  </button>
                  <button className="inline-flex items-center gap-2 px-6 py-2.5 bg-background-100 text-foreground-700 rounded-full text-sm font-medium hover:bg-background-200 transition-all whitespace-nowrap cursor-pointer">
                    <i className="ri-bookmark-line"></i>
                    收藏漫画
                  </button>
                </div>
              </div>
            </div>

            <section className="mb-14">
              <h2 className="text-xl font-bold font-heading text-foreground-950 mb-6">
                章节目录
              </h2>

              <div className="bg-background-100 rounded-xl p-4 md:p-6">
                <div className="flex flex-wrap items-center justify-between mb-4 gap-3">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-foreground-500">排序：</span>
                    <button className="px-3 py-1.5 bg-background-50 text-foreground-800 rounded-full text-xs font-medium cursor-pointer whitespace-nowrap">
                      正序 ↓
                    </button>
                  </div>
                  <span className="text-xs text-foreground-500">
                    共 {comic.chapters_list.length} 话 | 第 {startIdx + 1}-{Math.min(startIdx + chaptersPerPage, comic.chapters_list.length)} 话
                  </span>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
                  {displayedChapters.map((ch) => (
                    <a
                      key={ch.num}
                      href="#"
                      className="block px-3 py-2 rounded-lg bg-background-50 hover:bg-primary-100 hover:text-primary-600 transition-colors text-sm text-foreground-700 text-center"
                      onClick={(e) => e.preventDefault()}
                    >
                      {ch.title}
                    </a>
                  ))}
                </div>

                {totalPages > 1 && (
                  <div className="flex items-center justify-center gap-2 mt-6">
                    <button
                      className="w-8 h-8 flex items-center justify-center rounded-lg bg-background-50 text-foreground-500 hover:bg-background-200 transition-colors disabled:opacity-40 cursor-pointer"
                      disabled={currentPage === 1}
                      onClick={() => setCurrentPage(currentPage - 1)}
                    >
                      <i className="ri-arrow-left-s-line"></i>
                    </button>
                    {pageNumbers.map((p) => (
                      <button
                        key={p}
                        className={`w-8 h-8 flex items-center justify-center rounded-lg text-sm font-medium transition-colors cursor-pointer ${
                          p === currentPage
                            ? 'bg-primary-500 text-white'
                            : 'bg-background-50 text-foreground-600 hover:bg-background-200'
                        }`}
                        onClick={() => setCurrentPage(p)}
                      >
                        {p}
                      </button>
                    ))}
                    <button
                      className="w-8 h-8 flex items-center justify-center rounded-lg bg-background-50 text-foreground-500 hover:bg-background-200 transition-colors disabled:opacity-40 cursor-pointer"
                      disabled={currentPage === totalPages}
                      onClick={() => setCurrentPage(currentPage + 1)}
                    >
                      <i className="ri-arrow-right-s-line"></i>
                    </button>
                  </div>
                )}
              </div>
            </section>

            {relatedComics.length > 0 && (
              <section className="pb-14">
                <h2 className="text-xl font-bold font-heading text-foreground-950 mb-6">
                  同类型推荐
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                  {relatedComics.map((rc) => (
                    <Link
                      key={rc.id}
                      to={`/comic/${rc.id}`}
                      className="group flex gap-4 p-3 rounded-xl bg-background-100 hover:bg-background-200 transition-colors"
                    >
                      <div className="w-16 flex-shrink-0 rounded-lg overflow-hidden">
                        <img
                          src={rc.cover}
                          alt={rc.title}
                          className="w-full aspect-[3/4] object-cover object-top"
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors truncate">
                          {rc.title}
                        </h3>
                        <div className="flex items-center gap-2 mt-1 text-xs text-foreground-500">
                          <span className="flex items-center gap-0.5">
                            <i className="ri-star-fill text-[10px] text-accent-500"></i>
                            {rc.rating}
                          </span>
                          <span>{getCategoryName(rc.category)}</span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </section>
            )}
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}